import React from 'react';
import { Code2, KeyRound, Sparkles, FileSignature, ShieldCheck, MessageSquare } from 'lucide-react';

/**
 * Admin-only API documentation page. Static reference — no LLM cost.
 * Covers the most-commonly-queried API contracts that were ambiguous or
 * underdocumented (AI Council response shape, Khadija invoice payload,
 * Impersonation header, etc.).
 */
export default function APIDocs() {
  return (
    <div className="ve-section" data-testid="api-docs-page">
      <div className="ve-eyebrow flex items-center gap-2">
        <Code2 size={14} className="text-[#8B3FC5]"/>
        Internal API Reference
      </div>
      <h1 className="display text-3xl md:text-4xl mt-2 font-bold">
        Vigilant Entities API <span className="ve-gold-text">— for builders.</span>
      </h1>
      <p className="text-sm text-slate-600 dark:text-white/70 mt-2 max-w-3xl">
        Admin-only reference for integration partners. All endpoints require a bearer JWT cookie
        from <code>POST /api/auth/login</code> or a session cookie set by the Google handshake.
      </p>

      <Section icon={KeyRound} title="Authentication & Impersonation">
        <Endpoint method="POST" path="/api/auth/login" desc="Exchange email+password for a JWT and HTTP-only cookie.">
{`POST /api/auth/login
Content-Type: application/json

{ "email": "ceo@toothrocket.com", "password": "Vigilant@2026" }

→ 200 OK
{ "token": "<JWT>", "user": { ...UserPublic } }`}
        </Endpoint>
        <Endpoint method="GET" path="/api/auth/me" desc="Returns the current user plus impersonation state.">
{`GET /api/auth/me
→ 200 OK
{
  "user_id": "user_…",
  "email": "ceo@toothrocket.com",
  "role": "system_admin",            // = effective_role for this request
  "real_role": "system_admin",       // never changes during a session
  "effective_role": "system_admin",  // mirrors X-Impersonate-Role when set
  "impersonating": false
}`}
        </Endpoint>
        <Endpoint method="POST" path="/api/auth/impersonate" desc="Audit-only marker — actual role swap is per-request via header.">
{`POST /api/auth/impersonate
{ "target_role": "founder" }   // pass "system_admin" to exit
→ 200 OK { "ok": true, "target_role": "founder" }

# Then every subsequent request:
Headers: X-Impersonate-Role: founder
# (Header is silently IGNORED unless caller is system_admin.)`}
        </Endpoint>
      </Section>

      <Section icon={Sparkles} title="AI Council deliberation">
        <Endpoint method="POST" path="/api/ai/council/discuss" desc="Convene 2-5 personas + a moderator who synthesises.">
{`POST /api/ai/council/discuss
{
  "personas": ["yusuf", "khadija", "maryam"],
  "moderator": "yusuf",
  "question": "Should we accept a $5M strategic investment from a Singapore family office requesting a board seat?"
}

→ 200 OK
{
  "council_id":   "council_…",
  "tenant_id":    "primary",
  "question":     "…",
  "moderator":    "yusuf",
  "members":      ["yusuf","khadija","maryam"],
  "answers":      [ { "persona":"yusuf",   "answer":"…" }, … ],
  "synthesis_raw":"…",
  "synthesis": {
    "position":     "Accept with structural protections.",
    "rationale":    ["bullet 1", "bullet 2", …],
    "dissents":     ["legal_carve_out_concern", …],
    "next_actions": ["draft term sheet", "schedule diligence call", …],
    "confidence":   78          // 0-100
  }
}`}
        </Endpoint>
        <Tip>
          ⚠️ <b>Top-level fields are metadata.</b> Always read the substantive decision from
          <code className="ml-1 px-1 bg-slate-100 dark:bg-white/10 rounded">synthesis.&#123;position, rationale, dissents, next_actions, confidence&#125;</code>.
        </Tip>
      </Section>

      <Section icon={FileSignature} title="AI Drafts — Khadija invoice">
        <Endpoint method="POST" path="/api/ai/agents/khadija/draft-invoice" desc="Generate an invoice draft (items + VAT + total).">
{`POST /api/ai/agents/khadija/draft-invoice
{
  "counterparty_name": "Acme Corp",         // REQUIRED
  "purpose":           "Q1 consulting",     // REQUIRED
  "items": [                                 // REQUIRED — non-empty array
    { "description": "Strategy retainer", "quantity": 1, "unit_price": 50000 }
  ],
  "currency": "QAR"                          // optional, defaults QAR
}

→ 200 OK
{
  "invoice_id": "invoice_…",
  "status": "draft",
  "items": […],
  "subtotal": 50000,
  "vat": 0,
  "total": 50000,
  "currency": "QAR",
  "drafted_by": "khadija",
  "drafted_at": "2026-06-01T…Z"
}`}
        </Endpoint>
        <Tip>
          ⚠️ The fields are <b><code>counterparty_name</code></b> + <b><code>purpose</code></b> — NOT <code>customer</code>.
          422 errors usually mean these two are missing.
        </Tip>
      </Section>

      <Section icon={Sparkles} title="AI Mentorship (Shu'aib & Idris)">
        <Endpoint method="GET" path="/api/ai/mentors/status" desc="Full curriculum dashboard — all 15 AIs with age, focus, recent lessons.">
{`GET /api/ai/mentors/status
→ 200 OK
{
  "personas": [
    {
      "persona":         "yusuf",
      "field":           "Strategic Leadership",
      "age_years":       1020,
      "cycles_completed":2,
      "current_focus":   "Synthesising one millennium of strategic leadership…",
      "recent_lessons":  [ { "new_principle": "…", "created_at": "…" }, … ]
    },
    …
  ],
  "initial_age_years":       1000,
  "strategic_context_docs":  [ { "doc_id": "qnds3_2024_2030", … } ],
  "total_lessons_logged":    30
}`}
        </Endpoint>
        <Endpoint method="POST" path="/api/ai/mentors/train-now" desc="Run ONE training cycle. Advances every AI by +10 years. 15 short LLM calls.">
{`POST /api/ai/mentors/train-now
→ 200 OK
{ "cycles": 1, "personas": { "yusuf": { "age": 1010, "lesson": "…" }, … } }`}
        </Endpoint>
        <Endpoint method="POST" path="/api/ai/mentors/speed-train" desc="Run N (1-10) cycles back-to-back for demo aging.">
{`POST /api/ai/mentors/speed-train
{ "cycles": 5 }
→ 200 OK   (responds after all cycles complete; ~30s × 5 = 2-3 min)`}
        </Endpoint>
      </Section>

      <Section icon={ShieldCheck} title="Security & audit">
        <Endpoint method="GET" path="/api/admin/security" desc="Full security posture: Dawud middleware, Harun last scan, backups.">
{`GET /api/admin/security
→ 200 OK
{ "in_transit": "TLS 1.3", "at_rest_cipher": "AES-128-CBC + HMAC",
  "password_hash": "bcrypt", "smtp_configured": true,
  "harun_last_scan": {…}, "dawud_findings_24h": 0, "backups_last": "…" }`}
        </Endpoint>
        <Endpoint method="GET" path="/api/audit/?action=role_impersonation" desc="Every impersonation switch is logged.">
{`GET /api/audit/?action=role_impersonation&limit=50
→ 200 OK
{ "items": [ { "action":"role_impersonation", "actor_email":"ceo@toothrocket.com",
               "metadata":{"target_role":"founder","exit":false}, … }, … ] }`}
        </Endpoint>
      </Section>

      <Section icon={MessageSquare} title="Tips for clean integrations">
        <ul className="list-disc pl-5 text-sm text-slate-700 dark:text-white/80 space-y-1.5">
          <li>Always send the bearer token in <code>Authorization: Bearer &lt;jwt&gt;</code> — cookies work, but headers are simpler for server-to-server.</li>
          <li><b>Dawud security middleware</b> scans path + query + headers (not body) for injection patterns. Score ≥ 80 returns 400 &ldquo;Request blocked by Dawud&rdquo;.</li>
          <li>Tenant isolation: every collection has <code>tenant_id</code>. <code>system_admin</code> is the only role that sees across tenants.</li>
          <li>Datetime fields are ISO-8601 strings (timezone-aware). Send them the same way.</li>
        </ul>
      </Section>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="mt-8">
      <div className="ve-eyebrow text-[#5E1F8B] flex items-center gap-2">
        <Icon size={14}/> {title}
      </div>
      <div className="mt-3 space-y-3">{children}</div>
    </div>
  );
}

function Endpoint({ method, path, desc, children }) {
  const color = {
    GET:    'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
    POST:   'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
    PATCH:  'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    DELETE: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  }[method] || 'bg-slate-100 text-slate-800';
  return (
    <div className="ve-card p-4" data-testid={`api-endpoint-${path.replace(/[/{}:]/g, '-')}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-[0.18em] ${color}`}>{method}</span>
        <code className="text-sm font-mono">{path}</code>
      </div>
      <div className="mt-1 text-xs text-slate-500 dark:text-white/60">{desc}</div>
      <pre className="mt-3 text-[11px] leading-snug bg-slate-50 dark:bg-white/5 rounded p-3 overflow-x-auto font-mono whitespace-pre-wrap">{children}</pre>
    </div>
  );
}

function Tip({ children }) {
  return (
    <div className="ve-card p-3 border-l-4 border-l-[#8B3FC5] bg-[#FFF8E5] dark:bg-[#3a2f0a]">
      <div className="text-xs text-[#7A5A00] dark:text-[#F5E1A4] leading-relaxed">{children}</div>
    </div>
  );
}
